import { useEffect, useRef, useState, useCallback } from "react";

// Hook for scroll-based parallax with more dramatic effect
export function useScrollParallax(speed = 0.5) {
  const ref = useRef(null);
  const [transform, setTransform] = useState({ y: 0, opacity: 1, scale: 1 });

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementCenter = rect.top + rect.height / 2;
      const windowCenter = windowHeight / 2;
      const distance = elementCenter - windowCenter;
      
      // Calculate parallax offset
      const y = distance * speed;
      
      // Calculate opacity based on position
      const visibleRatio = 1 - Math.abs(distance) / windowHeight;
      const opacity = Math.max(0, Math.min(1, visibleRatio * 1.5));
      
      // Slight scale effect
      const scale = 0.95 + (visibleRatio * 0.05);
      
      setTransform({ y, opacity, scale });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [speed]);

  return { ref, transform };
}

// Parallax wrapper component for any content
export function ParallaxWrapper({ 
  children, 
  speed = 0.15, 
  className = "",
  fadeIn = true,
  scaleEffect = false 
}) {
  const { ref, transform } = useScrollParallax(speed);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        transform: `translateY(${transform.y}px) ${scaleEffect ? `scale(${transform.scale})` : ''}`,
        opacity: fadeIn ? transform.opacity : 1,
        transition: "transform 0.1s cubic-bezier(0.33, 1, 0.68, 1), opacity 0.3s ease-out",
        willChange: "transform, opacity",
      }}
    >
      {children}
    </div>
  );
}

// Parallax image with more dramatic movement
export function ParallaxImage({ 
  src, 
  alt, 
  className = "", 
  speed = 0.4,
  containerClassName = "",
  scale = 1.1  // Default scale, can be adjusted per image
}) {
  const containerRef = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      if (rect.top < windowHeight && rect.bottom > 0) {
        const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
        // More dramatic offset - moves image 100px range
        setOffset((scrollProgress - 0.5) * 150 * speed);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [speed]);

  return (
    <div ref={containerRef} className={`overflow-hidden ${containerClassName}`}>
      <img
        src={src}
        alt={alt}
        className={`w-full h-[140%] object-cover ${className}`}
        style={{
          transform: `translateY(${offset}px) scale(${scale})`,
          transition: "transform 0.15s cubic-bezier(0.33, 1, 0.68, 1)",
          willChange: "transform",
        }}
      />
    </div>
  );
}

// Text that moves at different speed
export function ParallaxText({ children, speed = -0.1, className = "" }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      if (rect.top < windowHeight && rect.bottom > 0) {
        const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
        setOffset((scrollProgress - 0.5) * 100 * speed);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [speed]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        transform: `translateY(${offset}px)`,
        transition: "transform 0.1s cubic-bezier(0.33, 1, 0.68, 1)",
        willChange: "transform",
      }}
    >
      {children}
    </div>
  );
}

// Scroll-triggered fade in with slide
export function ScrollFadeIn({ 
  children, 
  className = "", 
  direction = "up", // up, down, left, right
  delay = 0,
  threshold = 0.2
}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  const getInitialTransform = () => {
    switch (direction) {
      case "up": return "translateY(60px)";
      case "down": return "translateY(-60px)";
      case "left": return "translateX(60px)";
      case "right": return "translateX(-60px)";
      default: return "translateY(60px)";
    }
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translate(0)" : getInitialTransform(),
        transition: `opacity 0.8s cubic-bezier(0.33, 1, 0.68, 1) ${delay}s, transform 0.8s cubic-bezier(0.33, 1, 0.68, 1) ${delay}s`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// Sticky section that content scrolls over
export function StickySection({ children, className = "" }) {
  return (
    <div className={`sticky top-0 ${className}`}>
      {children}
    </div>
  );
}
